// src/features/equipment/pages/EquipmentTemplateSelectionPage.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Stack,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import { useEquipmentTemplates } from '../hooks/useEquipmentTemplates';
import { getEquipmentImageByModel } from '../utils/getEquipmentImage';
import PageHeader from '../../../components/common/PageHeader';

export default function EquipmentTemplateSelectionPage() {
  const { t } = useTranslation(['equipment', 'common']);
  const navigate = useNavigate();
  const theme = useTheme();
  const { roomId } = useParams();
  // Set by flows like onboarding that want the user sent back after creating
  const returnTo = useLocation().state?.returnTo;
  const { navigateBack } = useNavigationStack();

  const { templates, templatesByType, equipmentTypes, loading, error, fetchTemplates } =
    useEquipmentTemplates();

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [variantDialogOpen, setVariantDialogOpen] = useState(false);

  // Filter state
  const [searchText, setSearchText] = useState('');
  const [equipmentTypeFilter, setEquipmentTypeFilter] = useState('');
  const [manufacturerFilter, setManufacturerFilter] = useState('');

  // Manufacturers filtered by currently selected equipment type
  const filteredManufacturers = useMemo(() => {
    const source = equipmentTypeFilter
      ? (templatesByType[equipmentTypeFilter] || [])
      : templates;
    return [...new Set(source.map((t) => t.manufacturer).filter(Boolean))].sort();
  }, [templates, templatesByType, equipmentTypeFilter]);

  // Apply client-side filters to grouped data
  const filteredByType = useMemo(() => {
    const result = {};
    const term = searchText.toLowerCase();

    Object.entries(templatesByType).forEach(([type, list]) => {
      if (equipmentTypeFilter && type !== equipmentTypeFilter) return;

      const filtered = list.filter((tmpl) => {
        if (manufacturerFilter && tmpl.manufacturer !== manufacturerFilter) return false;
        if (
          term &&
          !tmpl.name?.toLowerCase().includes(term) &&
          !tmpl.model?.toLowerCase().includes(term) &&
          !tmpl.manufacturer?.toLowerCase().includes(term)
        )
          return false;
        return true;
      });

      if (filtered.length > 0) result[type] = filtered;
    });

    return result;
  }, [templatesByType, equipmentTypeFilter, manufacturerFilter, searchText]);

  const handleTemplateSelect = useCallback((template) => {
    setSelectedTemplate(template);
    setSelectedVariant(null);

    if (template.variants && template.variants.length > 0) {
      setVariantDialogOpen(true);
    } else {
      navigateToCreate(template);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const navigateToCreate = useCallback(
    (template, variant = null) => {
      const templateData = {
        equipment_type: template.equipment_type,
        name: variant ? `${template.model} ${variant.edition}` : template.name,
        manufacturer: template.manufacturer,
        model: variant ? `${template.model} ${variant.edition}` : template.model,
        max_voltage_kv: template.max_voltage_kv,
        task_templates: template.task_templates?.map((tt) => tt.template_id) ?? [],
      };

      if (roomId) {
        navigate(`/rooms/${roomId}/equipment/create`, { state: { template: templateData, returnTo } });
      } else {
        navigate(`/equipment/create`, { state: { template: templateData, returnTo } });
      }
    },
    [navigate, roomId, returnTo]
  );

  const handleVariantSelect = useCallback((variant) => setSelectedVariant(variant), []);

  const handleContinueWithVariant = useCallback(() => {
    if (selectedTemplate && selectedVariant) {
      navigateToCreate(selectedTemplate, selectedVariant);
    }
  }, [selectedTemplate, selectedVariant, navigateToCreate]);

  const handleCloseVariantDialog = useCallback(() => {
    setVariantDialogOpen(false);
    setSelectedTemplate(null);
    setSelectedVariant(null);
  }, []);

  const handleCreateCustom = useCallback(() => {
    if (roomId && roomId !== 'undefined') {
      navigate(`/rooms/${roomId}/equipment/create`, { state: { returnTo } });
    } else {
      navigate(`/equipment/create`, { state: { returnTo } });
    }
  }, [navigate, roomId, returnTo]);

  return (
    <Box>
      {/* Header */}
      <PageHeader
        onBack={() => navigateBack(`/equipment`)}
        title={t('equipment:template_selection.select_equipment')}
        subtitle={t('equipment:template_selection.subtitle')}
      />

      {/* Search and Filter Bar */}
      <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
        <TextField
          label={t('common:buttons.search')}
          variant="outlined"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder={t('equipment:template_selection.search_placeholder')}
          sx={{ flex: '1 1 300px' }}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
        />

        <FormControl sx={{ flex: '0 1 200px', minWidth: 200 }}>
          <InputLabel id="equipment-template-selection-page-equipment-type-label">{t('equipment:labels.equipment_type')}</InputLabel>
          <Select labelId="equipment-template-selection-page-equipment-type-label"
            value={equipmentTypeFilter}
            onChange={(e) => {
              setEquipmentTypeFilter(e.target.value);
              setManufacturerFilter('');
            }}
            label={t('equipment:labels.equipment_type')}
          >
            <MenuItem value="">{t('equipment:template_selection.all_types')}</MenuItem>
            {equipmentTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {t(`equipment:equipment_types.${type}`, type)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ flex: '0 1 200px', minWidth: 200 }}>
          <InputLabel id="equipment-template-selection-page-manufacturer-label">{t('equipment:labels.manufacturer')}</InputLabel>
          <Select labelId="equipment-template-selection-page-manufacturer-label"
            value={manufacturerFilter}
            onChange={(e) => setManufacturerFilter(e.target.value)}
            label={t('equipment:labels.manufacturer')}
          >
            <MenuItem value="">{t('equipment:template_selection.all_manufacturers')}</MenuItem>
            {filteredManufacturers.map((mfr) => (
              <MenuItem key={mfr} value={mfr}>
                {mfr}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}

      {!loading && (
        <>
          <Alert severity="info" sx={{ mb: 4 }}>
            {t('equipment:messages.cant_find_equipment')}
          </Alert>

          <Stack spacing={5}>
            {/* Equipment type sections */}
            {Object.entries(filteredByType).sort(([a], [b]) => t(`equipment:equipment_types.${a}`, a).localeCompare(t(`equipment:equipment_types.${b}`, b))).map(([type, list]) => (
              <Box key={type}>
                <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
                  {t(`equipment:equipment_types.${type}`, type)}
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    overflowX: 'auto',
                    gap: 2,
                    pb: 2,
                    '&::-webkit-scrollbar': { height: 8 },
                    '&::-webkit-scrollbar-track': { backgroundColor: 'grey.100', borderRadius: 1 },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: 'grey.400',
                      borderRadius: 1,
                      '&:hover': { backgroundColor: 'grey.500' },
                    },
                  }}
                >
                  {list.map((template, index) => (
                    <Card
                      key={`${template.equipment_template_id}-${index}`}
                      sx={{
                        minWidth: 280,
                        maxWidth: 280,
                        flexShrink: 0,
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 },
                      }}
                    >
                      <CardActionArea onClick={() => handleTemplateSelect(template)}>
                        <Box
                          sx={{
                            height: 180,
                            bgcolor: 'background.paper',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: 2,
                          }}
                        >
                          {getEquipmentImageByModel(template.model) ? (
                            <Box
                              sx={{
                                width: '100%',
                                height: '100%',
                                backgroundImage: `url(${getEquipmentImageByModel(template.model)})`,
                                backgroundSize: 'contain',
                                backgroundPosition: 'center',
                                backgroundRepeat: 'no-repeat',
                              }}
                            />
                          ) : (
                            <Typography variant="h6" color="text.secondary" align="center">
                              {template.model}
                            </Typography>
                          )}
                        </Box>
                        <CardContent>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="h6" gutterBottom noWrap sx={{ flex: 1 }}>
                              {template.model}
                            </Typography>
                            {template.max_voltage_kv && (
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <svg
                                  fill={theme.palette.primary.main}
                                  width="13px"
                                  height="13px"
                                  viewBox="-8 0 512 512"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path d="M328.2 255.8h151.6c9.1 0 16.8-7.7 16.2-16.8-5.1-75.8-44.4-142.2-102.5-184.2-7.4-5.3-17.9-2.9-22.7 4.8L290.4 188c22.6 14.3 37.8 39.2 37.8 67.8zm-37.8 67.7c-12.3 7.7-26.8 12.4-42.4 12.4-15.6 0-30-4.7-42.4-12.4L125.2 452c-4.8 7.7-2.4 18.1 5.6 22.4C165.7 493.2 205.6 504 248 504s82.3-10.8 117.2-29.6c8-4.3 10.4-14.8 5.6-22.4l-80.4-128.5zM248 303.8c26.5 0 48-21.5 48-48s-21.5-48-48-48-48 21.5-48 48 21.5 48 48 48zm-231.8-48h151.6c0-28.6 15.2-53.5 37.8-67.7L125.2 59.7c-4.8-7.7-15.3-10.2-22.7-4.8C44.4 96.9 5.1 163.3 0 239.1c-.6 9 7.1 16.7 16.2 16.7z" />
                                </svg>
                                {/* eslint-disable-next-line i18next/no-literal-string */}
                                <Typography variant="body2">{template.max_voltage_kv} Kv</Typography>
                              </Box>
                            )}
                          </Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom noWrap>
                            {template.manufacturer}
                          </Typography>
                          <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {template.variants?.length > 0 && (
                              <Chip
                                label={t('equipment:template_selection.editions_count', {
                                  count: template.variants.length,
                                })}
                                size="small"
                                color="secondary"
                                variant="outlined"
                              />
                            )}
                            {template.task_templates?.length > 0 && (
                              <Chip
                                label={t('equipment:template_selection.tasks_count', {
                                  count: template.task_templates.length,
                                })}
                                size="small"
                                color="info"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  ))}
                </Box>
              </Box>
            ))}

            {/* No results */}
            {Object.keys(filteredByType).length === 0 && !loading && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="text.secondary">
                  {t('equipment:template_selection.no_results')}
                </Typography>
              </Box>
            )}

            {/* Custom equipment section */}
            <Box>
              <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
                {t('equipment:template_selection.custom_equipment')}
              </Typography>
              <Card
                sx={{
                  maxWidth: 280,
                  border: '2px dashed',
                  borderColor: 'grey.300',
                  bgcolor: 'grey.50',
                  transition: 'all 0.2s',
                  '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.50' },
                }}
              >
                <CardActionArea onClick={handleCreateCustom} sx={{ minHeight: 300 }}>
                  <Box
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 3,
                    }}
                  >
                    <AddIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" gutterBottom align="center">
                      {t('equipment:template_selection.create_custom')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" align="center">
                      {t('equipment:template_selection.create_custom_description')}
                    </Typography>
                  </Box>
                </CardActionArea>
              </Card>
            </Box>
          </Stack>
        </>
      )}

      {/* Variant Selection Dialog */}
      <Dialog
        open={variantDialogOpen}
        onClose={handleCloseVariantDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t('equipment:template_selection.select_edition', {
            model: selectedTemplate?.model,
          })}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            {selectedTemplate?.variants?.map((variant, index) => (
              <Card
                key={index}
                sx={{
                  cursor: 'pointer',
                  border: 2,
                  borderColor: selectedVariant === variant ? 'primary.main' : 'grey.300',
                  bgcolor: selectedVariant === variant ? 'primary.50' : 'white',
                  transition: 'all 0.2s',
                  '&:hover': { borderColor: 'primary.main' },
                }}
                onClick={() => handleVariantSelect(variant)}
              >
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('equipment:template_selection.edition_label', {
                      edition: variant.edition,
                    })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {variant.notes}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseVariantDialog}>{t('common:buttons.cancel')}</Button>
          <Button
            variant="contained"
            disabled={!selectedVariant}
            onClick={handleContinueWithVariant}
          >
            {t('equipment:template_selection.continue')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
